import { FC, useMemo } from "react";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, X } from "lucide-react";
import { useSearchPage } from "./SearchPage.hooks";
import { cn } from "@/utils/tailwind/utils";
import { getStartSpecialCharacter } from "@/utils/suggestion";
import { PopoverAnchor } from "@radix-ui/react-popover";

type KeyphraseInputProps = Pick<
  ReturnType<typeof useSearchPage>,
  | "form"
  | "keyphraseComboOpen"
  | "setKeyphraseComboOpen"
  | "onChangeKeyphrase"
  | "onClearKeyphrase"
  | "suggestions"
  | "onSubmit"
  | "toast"
  | "t"
  | "convertCommandFormOptions"
  | "onSelectConvertCommand"
> & {
  shadow?: boolean;
  tooltip: JSX.Element;
};

export const KeyphraseInput: FC<KeyphraseInputProps> = (props) => {
  const {
    form,
    keyphraseComboOpen,
    setKeyphraseComboOpen,
    onChangeKeyphrase,
    onClearKeyphrase,
    onSubmit,
    suggestions,
    shadow,
    tooltip,
    t,
    onSelectConvertCommand,
    convertCommandFormOptions,
  } = props;

  const keyphrase = form.watch("keyphrase", "") ?? "";
  const phrases = useMemo(() => keyphrase.split("　").join(" ").split(" "), [keyphrase]);

  const popoverAnchorClassname = shadow && !keyphraseComboOpen ? "shadow-md" : "shadow-none";
  const popoverContentClassname = shadow ? "shadow-md" : "shadow-none";

  return (
    <Popover open={keyphraseComboOpen} onOpenChange={setKeyphraseComboOpen}>
      <PopoverAnchor
        className={cn(
          "flex h-10 w-[calc(100vw-96px)] flex-row items-center gap-x-2 rounded-md border bg-white pr-3 shadow-none focus:outline-none sm:w-[450px] sm:max-w-[calc(100vw-336px)]",
          popoverAnchorClassname,
        )}
      >
        <PopoverTrigger className="flex h-full flex-grow flex-row items-center gap-x-2 overflow-hidden pl-3">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <span className="mt-[1px] flex-grow overflow-hidden whitespace-nowrap pr-8 text-start text-sm">
            {keyphrase}
          </span>
        </PopoverTrigger>
        <div className="flex flex-row items-center gap-x-2">
          {keyphrase.length > 0 && (
            <>
              <button onClick={onClearKeyphrase}>
                <X className="h-4 w-4 opacity-50" />
              </button>
              <div className="h-[24px] border-r" />
            </>
          )}
          {tooltip}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className={cn(
          "-mt-11 w-[calc(100vw-96px)] p-0 shadow-none sm:w-[450px] sm:max-w-[calc(100vw-336px)]",
          popoverContentClassname,
        )}
      >
        <Command shouldFilter={false}>
          <div className="flex flex-row">
            <form className="flex-grow" action="#">
              <CommandInput
                className="h-[38px]"
                value={keyphrase}
                onFocus={(e) => {
                  requestAnimationFrame(() => {
                    e.target.setSelectionRange(keyphrase.length, keyphrase.length);
                  });
                }}
                onValueChange={(value) => {
                  onChangeKeyphrase(value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                    setKeyphraseComboOpen(false);
                    onSubmit();
                  }
                }}
              />
            </form>
            {/* NOTE: commandInput中のpx-3 border-bのカスタムがclassName指定でできなかったので、以下のタグに指定して回避  */}
            <div className="flex items-center border-b pr-3">
              <div className="flex flex-row items-center gap-x-2">
                {keyphrase.length > 0 && (
                  <>
                    <button onClick={onClearKeyphrase}>
                      <X className="h-4 w-4 opacity-50" />
                    </button>
                    <div className="h-[24px] border-r" />
                  </>
                )}
                {tooltip}
              </div>
            </div>
          </div>

          <CommandList className={suggestions.length === 0 ? "-mt-[1px]" : ""}>
            {keyphrase.length > 0 && (
              <CommandGroup heading={t("search_form_option_heading_convert")}>
                <CommandItem key="-1" className="hidden" value={"none"} />
                {convertCommandFormOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    disabled={phrases.length <= 1 ? !option.enabledSingleWord : false}
                    onSelect={() => {
                      onSelectConvertCommand(option.value);
                      setKeyphraseComboOpen(false);
                      onSubmit();
                    }}
                  >
                    <span>{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {suggestions.length > 0 && (
              <CommandGroup
                className={suggestions.length === 0 ? "p-0" : ""}
                heading={t("search_form_option_heading_suggestions")}
              >
                {/* Command はデフォルトで CommandList の先頭行が選択状態になるのと、
            Enter押下時にイベント発火させるため、入力値を CommandItem hidden で持たせておく。 */}
                <CommandItem
                  key="-1"
                  className="hidden"
                  value={encodeURIComponent(keyphrase)}
                  onSelect={() => {
                    onChangeKeyphrase(keyphrase);
                    setKeyphraseComboOpen(false);
                    onSubmit();
                  }}
                >
                  {keyphrase}
                </CommandItem>
                {suggestions.map((suggestion, index) => (
                  <CommandItem
                    key={index}
                    value={suggestion}
                    onSelect={() => {
                      // NOTE: 最初の文字に特殊文字がある場合は選択したSuggestionの文字にも反映
                      const specialCharacter = getStartSpecialCharacter(keyphrase);
                      const selectedSuggestion = specialCharacter + suggestion + specialCharacter;
                      onChangeKeyphrase(selectedSuggestion);
                      setKeyphraseComboOpen(false);
                      onSubmit();
                    }}
                  >
                    {suggestion}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
